// src/client/PayableAccounts.jsx
import { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import {
  collection, addDoc, updateDoc, deleteDoc,
  doc, onSnapshot, serverTimestamp, orderBy, query, where
} from "firebase/firestore";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";
import { Download, Plus, Search, Filter, Trash2, AlertCircle } from "lucide-react";
import * as XLSX from "xlsx";
ChartJS.register(ArcElement, Tooltip, Legend);

import useCurrentUser from "../hooks/useCurrentUser";

const CURRENCIES = ["AUD", "USD", "EUR", "JPY"];
const PAYABLE_TYPES = [
  { key: "import", label: "Import Fee" },
  { key: "export", label: "Export Fee" },
  { key: "3pl", label: "3PL Weekly" },
  { key: "transport", label: "Transport" },
];
const PAYABLE_STATUSES = ["open", "approved", "paid", "disputed"];
const RECEIVABLE_STATUSES = ["draft", "sent", "paid", "overdue"];

function fmtMoney(n, cur = "AUD") {
  const num = Number(n || 0);
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: cur }).format(num);
  } catch {
    return `${cur} ${num.toFixed(2)}`;
  }
}
function parseAmount(v) {
  if (typeof v === "number") return v;
  const n = parseFloat(String(v || "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}
function isThisWeek(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  const start = new Date(now);
  start.setDate(now.getDate() - ((now.getDay() + 6) % 7)); // Monday
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(start.getDate() + 7);
  return d >= start && d < end;
}

export default function PayableAccounts() {
  const { clientId } = useCurrentUser();
  const [tab, setTab] = useState("overview"); // overview | payables | receivables
  const [payables, setPayables] = useState([]);
  const [receivables, setReceivables] = useState([]);

  // filters
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // modals/forms
  const [showNewPayable, setShowNewPayable] = useState(false);
  const [showNewInvoice, setShowNewInvoice] = useState(false);
  const [draftPayable, setDraftPayable] = useState({
    type: "3pl", vendor: "", ref: "", description: "",
    amount: "", currency: "AUD", dueDate: "", status: "open"
  });
  const [draftInvoice, setDraftInvoice] = useState({
    customer: "", invoiceNo: "", description: "",
    amount: "", currency: "AUD", dueDate: "", status: "draft", email: ""
  });

  // live data
  useEffect(() => {
    if (!clientId) return;
    const unsub1 = onSnapshot(
      query(collection(db, "payments"), where("companyId", "==", clientId), orderBy("createdAt", "asc")),
      (snap) => setPayables(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    const unsub2 = onSnapshot(
      query(collection(db, "invoices"), where("companyId", "==", clientId), orderBy("createdAt", "asc")),
      (snap) => setReceivables(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    return () => { unsub1(); unsub2(); };
  }, [clientId]);

  // derived
  const filteredPayables = useMemo(() => {
    const q = search.trim().toLowerCase();
    return payables.filter(p =>
      (!typeFilter || p.type === typeFilter) &&
      (!statusFilter || p.status === statusFilter) &&
      (
        !q ||
        [p.vendor, p.ref, p.description, p.currency].some(v => String(v || "").toLowerCase().includes(q))
      )
    );
  }, [payables, search, typeFilter, statusFilter]);

  const filteredReceivables = useMemo(() => {
    const q = search.trim().toLowerCase();
    return receivables.filter(r =>
      (!statusFilter || r.status === statusFilter) &&
      (!q || [r.customer, r.invoiceNo, r.description, r.currency, r.email].some(v => String(v || "").toLowerCase().includes(q)))
    );
  }, [receivables, search, statusFilter]);

  const totals = useMemo(() => {
    const pOpen = payables.filter(p => p.status !== "paid").reduce((s, p) => s + parseAmount(p.amount), 0);
    const pWeek3pl = payables
      .filter(p => p.type === "3pl" && isThisWeek(p.dueDate))
      .reduce((s, p) => s + parseAmount(p.amount), 0);
    const pWeekTransport = payables
      .filter(p => p.type === "transport" && isThisWeek(p.dueDate))
      .reduce((s, p) => s + parseAmount(p.amount), 0);

    const rOutstanding = receivables
      .filter(r => r.status === "sent" || r.status === "overdue")
      .reduce((s, r) => s + parseAmount(r.amount), 0);

    return { pOpen, pWeek3pl, pWeekTransport, rOutstanding };
  }, [payables, receivables]);

  // charts
  const payablesByType = useMemo(() => {
    const counts = PAYABLE_TYPES.map(t => ({
      label: t.label,
      value: payables.filter(p => p.type === t.key).reduce((s, p) => s + parseAmount(p.amount), 0)
    }));
    return {
      labels: counts.map(c => c.label),
      datasets: [{
        data: counts.map(c => Math.round(c.value * 100) / 100),
        backgroundColor: ["#0B1F33", "#D4AF37", "#64748b", "#10b981"], // Navy, Gold, Steel, Emerald
        borderWidth: 0
      }]
    };
  }, [payables]);

  // actions
  const savePayable = async () => {
    const payload = {
      ...draftPayable,
      amount: parseAmount(draftPayable.amount),
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    };
    await addDoc(collection(db, "payments"), { ...payload, companyId: clientId });
    setShowNewPayable(false);
    setDraftPayable({ type: "3pl", vendor: "", ref: "", description: "", amount: "", currency: "AUD", dueDate: "", status: "open" });
  };

  const saveInvoice = async () => {
    const payload = {
      ...draftInvoice,
      amount: parseAmount(draftInvoice.amount),
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      sentAt: null,
    };
    await addDoc(collection(db, "invoices"), { ...payload, companyId: clientId });
    setShowNewInvoice(false);
    setDraftInvoice({ customer: "", invoiceNo: "", description: "", amount: "", currency: "AUD", dueDate: "", status: "draft", email: "" });
  };

  const updatePayable = async (row, patch) => {
    await updateDoc(doc(db, "payments", row.id), {
      ...patch, updatedAt: serverTimestamp()
    });
  };

  const updateInvoice = async (row, patch) => {
    await updateDoc(doc(db, "invoices", row.id), {
      ...patch, updatedAt: serverTimestamp()
    });
  };

  const resendInvoice = async (row) => {
    // Here we just update sentAt; hook your email/Slack function server-side later
    await updateInvoice(row, { status: row.status === "draft" ? "sent" : row.status, sentAt: serverTimestamp() });
  };

  const exportCsv = (rows, filename) => {
    const header = Object.keys(rows[0] || {});
    const csv = [
      header.join(","),
      ...rows.map(r =>
        header.map(h => {
          const v = r[h] ?? "";
          const s = String(v);
          return s.match(/[,"\n]/) ? `"${s.replace(/"/g, '""')}"` : s;
        }).join(",")
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="p-8 space-y-8 max-w-[1600px] mx-auto pb-24">
      {/* Header */}
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-serif text-navy-900">Accounts</h1>
          <p className="text-slate-500 mt-1">Manage payables, receivables, and invoices.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setShowNewPayable(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-navy-900 font-semibold shadow-sm transition-colors">
            <Plus size={18} /> New Payable
          </button>
          <button onClick={() => setShowNewInvoice(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-navy-900 text-white hover:bg-navy-800 font-semibold shadow-lg shadow-navy-900/20 transition-all transform active:scale-95">
            <Plus size={18} /> New Invoice
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200">
        {["overview", "payables", "receivables"].map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 ${tab === t ? "border-navy-900 text-navy-900" : "border-transparent text-slate-500 hover:text-navy-700"
              } capitalize`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Controls (Global for simplification here, or specific to tabs) */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input
            placeholder="Search transactions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-4 py-2 rounded-lg bg-white border border-slate-200 focus:ring-2 focus:ring-navy-900/20 focus:border-navy-900 text-sm w-64 text-slate-700 placeholder:text-slate-400"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="pl-9 pr-8 py-2 rounded-lg bg-white border border-slate-200 focus:ring-2 focus:ring-navy-900/20 text-sm text-slate-700 appearance-none cursor-pointer"
          >
            <option value="">All Types</option>
            {PAYABLE_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 rounded-lg bg-white border border-slate-200 focus:ring-2 focus:ring-navy-900/20 text-sm text-slate-700 cursor-pointer"
        >
          <option value="">All Status</option>
          {Array.from(new Set([...PAYABLE_STATUSES, ...RECEIVABLE_STATUSES])).map(s =>
            <option key={s} value={s}>{s}</option>
          )}
        </select>
      </div>

      {/* Overview */}
      {tab === "overview" && (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 flex flex-col justify-between">
            <div>
              <div className="text-sm font-medium text-slate-500 uppercase tracking-wider">Open Payables</div>
              <div className="text-3xl font-bold text-navy-900 mt-2">{fmtMoney(totals.pOpen)}</div>
            </div>
            <div className="text-xs text-slate-400 mt-4 flex items-center gap-1">
              <AlertCircle size={14} /> Total outstanding bills
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 flex flex-col justify-between">
            <div>
              <div className="text-sm font-medium text-slate-500 uppercase tracking-wider">Due This Week (3PL)</div>
              <div className="text-3xl font-bold text-navy-900 mt-2">{fmtMoney(totals.pWeek3pl)}</div>
            </div>
            <div className="text-xs text-slate-400 mt-4">Warehouse & storage fees</div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 flex flex-col justify-between">
            <div>
              <div className="text-sm font-medium text-slate-500 uppercase tracking-wider">Due This Week (Transport)</div>
              <div className="text-3xl font-bold text-navy-900 mt-2">{fmtMoney(totals.pWeekTransport)}</div>
            </div>
            <div className="text-xs text-slate-400 mt-4">Freight & logistics fees</div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 lg:col-span-2">
            <h3 className="text-lg font-bold text-navy-900 mb-6 font-serif">Expenses Breakdown</h3>
            <div className="h-64 flex justify-center">
              <Pie data={payablesByType} options={{ maintainAspectRatio: false, plugins: { legend: { position: "right", labels: { usePointStyle: true, font: { family: 'Inter' } } } } }} />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 flex flex-col justify-between">
            <div>
              <div className="text-sm font-medium text-slate-500 uppercase tracking-wider">Receivables</div>
              <div className="text-3xl font-bold text-emerald-600 mt-2">{fmtMoney(totals.rOutstanding)}</div>
            </div>
            <div className="text-xs text-slate-400 mt-4">Invoices issued & pending payment</div>
          </div>
        </div>
      )}

      {/* Payables Table */}
      {tab === "payables" && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50/50">
            <h3 className="font-bold text-navy-900 uppercase tracking-wide text-sm">Payables List</h3>
            {!!filteredPayables.length && (
              <button
                onClick={() => exportCsv(filteredPayables, "payables.csv")}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
              >
                <Download size={14} /> Export CSV
              </button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-xs font-bold text-navy-900 uppercase tracking-wider">
                <tr>
                  <th className="px-6 py-3">Type</th>
                  <th className="px-6 py-3">Vendor</th>
                  <th className="px-6 py-3">Ref</th>
                  <th className="px-6 py-3">Description</th>
                  <th className="px-6 py-3">Amount</th>
                  <th className="px-6 py-3">Currency</th>
                  <th className="px-6 py-3">Due</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredPayables.map((p) => (
                  <tr key={p.id} className="hover:bg-slate-50 transition-colors group">
                    <td className="px-6 py-4 capitalize font-medium text-slate-700">{p.type}</td>
                    <td className="px-6 py-4 text-slate-600">{p.vendor}</td>
                    <td className="px-6 py-4 text-slate-600">{p.ref}</td>
                    <td className="px-6 py-4 text-slate-500 truncate max-w-[200px]">{p.description}</td>
                    <td className="px-6 py-4 font-bold text-navy-900">{fmtMoney(p.amount, p.currency)}</td>
                    <td className="px-6 py-4 text-slate-500 text-xs">{p.currency}</td>
                    <td className="px-6 py-4 text-slate-600 font-mono text-xs">{p.dueDate || "-"}</td>
                    <td className="px-6 py-4">
                      <StatusBadge status={p.status} />
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2 opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity">
                        {p.status === "open" && (
                          <button onClick={() => updatePayable(p, { status: "approved" })} className="text-xs bg-blue-50 text-blue-600 px-3 py-2 md:px-2 md:py-1 rounded-lg hover:bg-blue-100 font-medium">Approve</button>
                        )}
                        {p.status !== "paid" && (
                          <button onClick={() => updatePayable(p, { status: "paid" })} className="text-xs bg-emerald-50 text-emerald-600 px-3 py-2 md:px-2 md:py-1 rounded-lg hover:bg-emerald-100 font-medium">Pay</button>
                        )}
                        <button onClick={() => updatePayable(p, { status: "disputed" })} className="p-2 md:p-1.5 text-slate-400 hover:text-red-500 rounded-lg hover:bg-red-50" title="Dispute">
                          <AlertCircle size={16} className="md:w-3.5 md:h-3.5" />
                        </button>
                        <button onClick={() => deleteDoc(doc(db, "payments", p.id))} className="p-2 md:p-1.5 text-slate-400 hover:text-red-500 rounded-lg hover:bg-red-50" title="Delete">
                          <Trash2 size={16} className="md:w-3.5 md:h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!filteredPayables.length && (
                  <tr><td colSpan={9} className="px-6 py-12 text-center text-slate-400 italic">No payables match your filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Receivables / Invoices Table */}
      {tab === "receivables" && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50/50">
            <h3 className="font-bold text-navy-900 uppercase tracking-wide text-sm">Invoices Issued</h3>
            {!!filteredReceivables.length && (
              <button
                onClick={() => exportCsv(filteredReceivables, "receivables.csv")}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
              >
                <Download size={14} /> Export CSV
              </button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-xs font-bold text-navy-900 uppercase tracking-wider">
                <tr>
                  <th className="px-6 py-3">Customer</th>
                  <th className="px-6 py-3">Invoice #</th>
                  <th className="px-6 py-3">Description</th>
                  <th className="px-6 py-3">Amount</th>
                  <th className="px-6 py-3">Currency</th>
                  <th className="px-6 py-3">Due</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredReceivables.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50 transition-colors group">
                    <td className="px-6 py-4 font-bold text-navy-900">{r.customer}</td>
                    <td className="px-6 py-4 font-mono text-slate-600">{r.invoiceNo}</td>
                    <td className="px-6 py-4 text-slate-500 truncate max-w-[200px]">{r.description}</td>
                    <td className="px-6 py-4 font-bold text-emerald-600">{fmtMoney(r.amount, r.currency)}</td>
                    <td className="px-6 py-4 text-slate-500 text-xs">{r.currency}</td>
                    <td className="px-6 py-4 text-slate-600 font-mono text-xs">{r.dueDate || "-"}</td>
                    <td className="px-6 py-4">
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2 opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity">
                        {r.status === "draft" && (
                          <button onClick={() => updateInvoice(r, { status: "sent" })} className="text-xs bg-blue-50 text-blue-600 px-3 py-2 md:px-2 md:py-1 rounded-lg hover:bg-blue-100 font-medium">Send</button>
                        )}
                        {r.status !== "paid" && (
                          <button onClick={() => updateInvoice(r, { status: "paid" })} className="text-xs bg-emerald-50 text-emerald-600 px-3 py-2 md:px-2 md:py-1 rounded-lg hover:bg-emerald-100 font-medium">Mark Paid</button>
                        )}
                        <button onClick={() => deleteDoc(doc(db, "invoices", r.id))} className="p-2 md:p-1.5 text-slate-400 hover:text-red-500 rounded-lg hover:bg-red-50" title="Delete">
                          <Trash2 size={16} className="md:w-3.5 md:h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!filteredReceivables.length && (
                  <tr><td colSpan={8} className="px-6 py-12 text-center text-slate-400 italic">No invoices found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* New Payable Modal */}
      {showNewPayable && (
        <div className="fixed inset-0 bg-navy-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
              <h3 className="text-lg font-bold text-navy-900 font-serif">Record Payable</h3>
              <button onClick={() => setShowNewPayable(false)} className="text-slate-400 hover:text-red-500 transition-colors">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <label className="text-sm">
                  <span className="block text-xs font-bold text-navy-900 uppercase tracking-wide mb-1">Type</span>
                  <select
                    value={draftPayable.type}
                    onChange={e => setDraftPayable(d => ({ ...d, type: e.target.value }))}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-slate-700 focus:ring-2 focus:ring-navy-900/20 focus:border-navy-900 transition-all font-medium"
                  >
                    {PAYABLE_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </label>
                <label className="text-sm">
                  <span className="block text-xs font-bold text-navy-900 uppercase tracking-wide mb-1">Vendor</span>
                  <input
                    value={draftPayable.vendor}
                    onChange={e => setDraftPayable(d => ({ ...d, vendor: e.target.value }))}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-slate-700 focus:ring-2 focus:ring-navy-900/20 focus:border-navy-900 transition-all"
                    placeholder="E.g., DHL Global"
                  />
                </label>
                <label className="text-sm">
                  <span className="block text-xs font-bold text-navy-900 uppercase tracking-wide mb-1">Reference</span>
                  <input
                    value={draftPayable.ref}
                    onChange={e => setDraftPayable(d => ({ ...d, ref: e.target.value }))}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-slate-700 focus:ring-2 focus:ring-navy-900/20 focus:border-navy-900 transition-all"
                    placeholder="Invoice # or PO #"
                  />
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <label className="text-sm">
                    <span className="block text-xs font-bold text-navy-900 uppercase tracking-wide mb-1">Amount</span>
                    <input
                      value={draftPayable.amount}
                      onChange={e => setDraftPayable(d => ({ ...d, amount: e.target.value }))}
                      type="number" step="0.01"
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-slate-700 focus:ring-2 focus:ring-navy-900/20 focus:border-navy-900 transition-all font-mono"
                      placeholder="0.00"
                    />
                  </label>
                  <label className="text-sm">
                    <span className="block text-xs font-bold text-navy-900 uppercase tracking-wide mb-1">Curr</span>
                    <select
                      value={draftPayable.currency}
                      onChange={e => setDraftPayable(d => ({ ...d, currency: e.target.value }))}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-slate-700 focus:ring-2 focus:ring-navy-900/20 focus:border-navy-900 transition-all"
                    >
                      {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </label>
                </div>
                <label className="text-sm">
                  <span className="block text-xs font-bold text-navy-900 uppercase tracking-wide mb-1">Due Date</span>
                  <input
                    value={draftPayable.dueDate}
                    onChange={e => setDraftPayable(d => ({ ...d, dueDate: e.target.value }))}
                    type="date"
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-slate-700 focus:ring-2 focus:ring-navy-900/20 focus:border-navy-900 transition-all"
                  />
                </label>
                <label className="text-sm md:col-span-2">
                  <span className="block text-xs font-bold text-navy-900 uppercase tracking-wide mb-1">Description</span>
                  <textarea
                    value={draftPayable.description}
                    onChange={e => setDraftPayable(d => ({ ...d, description: e.target.value }))}
                    rows={3}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-slate-700 focus:ring-2 focus:ring-navy-900/20 focus:border-navy-900 transition-all resize-none"
                    placeholder="Brief details about this bill..."
                  />
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 mt-4">
                <button onClick={() => setShowNewPayable(false)} className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 font-semibold transition-colors">Cancel</button>
                <button onClick={savePayable} className="px-5 py-2.5 rounded-xl bg-navy-900 text-white hover:bg-navy-800 font-semibold shadow-lg shadow-navy-900/20 transition-all transform active:scale-95">Record Bill</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Invoice Modal */}
      {showNewInvoice && (
        <div className="fixed inset-0 bg-navy-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
              <h3 className="text-lg font-bold text-navy-900 font-serif">Create Invoice</h3>
              <button onClick={() => setShowNewInvoice(false)} className="text-slate-400 hover:text-red-500 transition-colors">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <label className="text-sm">
                  <span className="block text-xs font-bold text-navy-900 uppercase tracking-wide mb-1">Customer</span>
                  <input
                    value={draftInvoice.customer}
                    onChange={e => setDraftInvoice(d => ({ ...d, customer: e.target.value }))}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-slate-700 focus:ring-2 focus:ring-navy-900/20 focus:border-navy-900 transition-all"
                    placeholder="Client Name"
                  />
                </label>
                <label className="text-sm">
                  <span className="block text-xs font-bold text-navy-900 uppercase tracking-wide mb-1">Invoice #</span>
                  <input
                    value={draftInvoice.invoiceNo}
                    onChange={e => setDraftInvoice(d => ({ ...d, invoiceNo: e.target.value }))}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-slate-700 focus:ring-2 focus:ring-navy-900/20 focus:border-navy-900 transition-all font-mono"
                    placeholder="INV-2024-001"
                  />
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <label className="text-sm">
                    <span className="block text-xs font-bold text-navy-900 uppercase tracking-wide mb-1">Amount</span>
                    <input
                      value={draftInvoice.amount}
                      onChange={e => setDraftInvoice(d => ({ ...d, amount: e.target.value }))}
                      type="number" step="0.01"
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-slate-700 focus:ring-2 focus:ring-navy-900/20 focus:border-navy-900 transition-all font-mono"
                      placeholder="0.00"
                    />
                  </label>
                  <label className="text-sm">
                    <span className="block text-xs font-bold text-navy-900 uppercase tracking-wide mb-1">Curr</span>
                    <select
                      value={draftInvoice.currency}
                      onChange={e => setDraftInvoice(d => ({ ...d, currency: e.target.value }))}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-slate-700 focus:ring-2 focus:ring-navy-900/20 focus:border-navy-900 transition-all"
                    >
                      {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </label>
                </div>
                <label className="text-sm">
                  <span className="block text-xs font-bold text-navy-900 uppercase tracking-wide mb-1">Due Date</span>
                  <input
                    value={draftInvoice.dueDate}
                    onChange={e => setDraftInvoice(d => ({ ...d, dueDate: e.target.value }))}
                    type="date"
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-slate-700 focus:ring-2 focus:ring-navy-900/20 focus:border-navy-900 transition-all"
                  />
                </label>
                <label className="text-sm md:col-span-2">
                  <span className="block text-xs font-bold text-navy-900 uppercase tracking-wide mb-1">Customer Email</span>
                  <input
                    value={draftInvoice.email}
                    onChange={e => setDraftInvoice(d => ({ ...d, email: e.target.value }))}
                    type="email"
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-slate-700 focus:ring-2 focus:ring-navy-900/20 focus:border-navy-900 transition-all"
                    placeholder="billing@customer.com"
                  />
                </label>
                <label className="text-sm md:col-span-2">
                  <span className="block text-xs font-bold text-navy-900 uppercase tracking-wide mb-1">Description</span>
                  <textarea
                    value={draftInvoice.description}
                    onChange={e => setDraftInvoice(d => ({ ...d, description: e.target.value }))}
                    rows={3}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-slate-700 focus:ring-2 focus:ring-navy-900/20 focus:border-navy-900 transition-all resize-none"
                    placeholder="Services rendered..."
                  />
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 mt-4">
                <button onClick={() => setShowNewInvoice(false)} className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 font-semibold transition-colors">Cancel</button>
                <button onClick={saveInvoice} className="px-5 py-2.5 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 font-semibold shadow-lg shadow-emerald-600/20 transition-all transform active:scale-95">Save Invoice</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const s = (status || "").toLowerCase();

  let classes = "bg-slate-100 text-slate-600 border-slate-200";

  // Paid
  if (s === "paid") classes = "bg-emerald-50 text-emerald-700 border-emerald-200";
  // Approved / Sent
  if (["approved", "sent"].includes(s)) classes = "bg-blue-50 text-blue-700 border-blue-200";
  // Open / Draft
  if (["open", "draft"].includes(s)) classes = "bg-slate-100 text-slate-600 border-slate-200";
  // Dispute / Overdue
  if (["disputed", "overdue"].includes(s)) classes = "bg-red-50 text-red-700 border-red-200";

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${classes} capitalize tracking-wide`}>
      {status || "Pending"}
    </span>
  );
}
